import React, { useContext } from 'react';
import { ActiveSessionContext } from './useActiveSession';

// ══════════════════════════════════════════════════════════════
// UI для активної сесії — банер (пасивний режим) + 3 модалки
// Читає стан із ActiveSessionContext (провайдер у App.js)
// ══════════════════════════════════════════════════════════════

function BaseModal({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function AutoActiveModal({ onClose }) {
  return (
    <BaseModal onClose={onClose}>
      <h2 className="text-lg font-bold mb-3" style={{ color: '#163248' }}>
        Це вікно активне
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-5">
        Ваш акаунт було відкрито в іншому браузері, але останні 15 хвилин там не було нових змін. Тому це вікно стало активним — дані зберігатимуться звідси. Інші вікна перейшли в пасивний режим.
      </p>
      <button
        onClick={onClose}
        className="w-full py-2.5 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
        style={{ background: '#163248' }}
      >
        Зрозуміло
      </button>
    </BaseModal>
  );
}

function AutoPassiveModal({ onClose }) {
  return (
    <BaseModal onClose={onClose}>
      <h2 className="text-lg font-bold mb-3" style={{ color: '#163248' }}>
        Пасивний режим
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-2">
        Ваш акаунт активний в іншому браузері і там зараз ведеться робота. Це вікно у пасивному режимі — взаємодія неможлива.
      </p>
      <p className="text-sm text-gray-600 leading-relaxed mb-5">
        Щоб зробити це вікно активним, зачекайте 15 хвилин без активності в іншому браузері, потім натисніть <strong>Стати активним</strong> у банері зверху.
      </p>
      <button
        onClick={onClose}
        className="w-full py-2.5 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
        style={{ background: '#163248' }}
      >
        Зрозуміло
      </button>
    </BaseModal>
  );
}

function CooldownModal({ onClose, minutesRemaining }) {
  return (
    <BaseModal onClose={onClose}>
      <h2 className="text-lg font-bold mb-3" style={{ color: '#163248' }}>
        Схоже, хтось працює в іншому вікні
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-5">
        Активне вікно недавно вносило зміни. Спробуйте ще раз приблизно через <strong>{minutesRemaining} хв</strong>.
      </p>
      <button
        onClick={onClose}
        className="w-full py-2.5 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
        style={{ background: '#163248' }}
      >
        Зрозуміло
      </button>
    </BaseModal>
  );
}

export function PassiveBanner() {
  const { isPassive, claimActivity } = useContext(ActiveSessionContext);
  if (!isPassive) return null;
  return (
    <div
      className="w-full flex items-center justify-between gap-3 px-4 py-2 text-white text-sm shadow-md"
      style={{ background: '#b45309', pointerEvents: 'auto' }}
    >
      <div className="flex items-center gap-2">
        <span aria-hidden="true">ⓘ</span>
        <span>
          <strong>Пасивний режим.</strong> Взаємодія неможлива. Щоб почати працювати — зробіть це вікно активним.
        </span>
      </div>
      <button
        onClick={claimActivity}
        className="whitespace-nowrap bg-white hover:bg-gray-100 px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
        style={{ color: '#b45309' }}
      >
        Стати активним
      </button>
    </div>
  );
}

// Всі три модалки в одному місці — знає що показати на основі modalToShow
export function ActiveSessionModals() {
  const { modalToShow, cooldownRemainingMin, dismissModal } = useContext(ActiveSessionContext);
  if (!modalToShow) return null;
  if (modalToShow === 'auto-active') return <AutoActiveModal onClose={dismissModal} />;
  if (modalToShow === 'auto-passive') return <AutoPassiveModal onClose={dismissModal} />;
  if (modalToShow === 'cooldown') return <CooldownModal onClose={dismissModal} minutesRemaining={cooldownRemainingMin} />;
  return null;
}

// CSS-обгортка для read-only коли пасивний
// Використовуй style замість className для точності
export function ReadOnlyWrapper({ children }) {
  const { isPassive } = useContext(ActiveSessionContext);
  return (
    <div
      style={isPassive ? {
        pointerEvents: 'none',
        opacity: 0.7,
        userSelect: 'text',
      } : {}}
    >
      {children}
    </div>
  );
}
